// src/services/classSessionsService.js
import apiClient from './api.js';

class ClassSessionsService {
  constructor() {
    // Cache for sessions data
    this.cache = new Map();
    // Cache TTL (time to live) - 5 minutes
    this.cacheTTL = 5 * 60 * 1000;
    // Track in-flight requests to prevent duplicates
    this.pendingRequests = new Map();
  }

  // Get all sessions for a specific class with caching and deduplication
  async getSessionsForClass(classId) {
    // Check cache first
    const cached = this.cache.get(classId);
    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      return cached.data;
    }

    // Check if request is already in flight
    if (this.pendingRequests.has(classId)) {
      return this.pendingRequests.get(classId);
    }

    // Create new request
    const requestPromise = this._fetchSessions(classId);
    this.pendingRequests.set(classId, requestPromise);

    try {
      const result = await requestPromise;

      // Cache the result
      this.cache.set(classId, {
        data: result,
        timestamp: Date.now()
      });

      return result;
    } finally {
      // Remove from pending requests
      this.pendingRequests.delete(classId);
    }
  }

  // Internal method to actually fetch sessions
  async _fetchSessions(classId) {
    try {
      const response = await apiClient.get(`/class-sessions/class/${classId}`);
      return {
        success: true,
        data: response.sessions || [],
        total: response.total || 0
      };
    } catch (error) {
      console.error('Error fetching class sessions:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  // Clear cache for a specific class or all classes
  clearCache(classId = null) {
    if (classId) {
      this.cache.delete(classId);
    } else {
      this.cache.clear();
    }
  }

  // Get all sessions for the current user (for schedule view)
  async getUserSessions(params = {}) {
    try {
      const response = await apiClient.get('/class-sessions/user', params);
      return {
        success: true,
        data: response.sessions || [],
        total: response.total || 0
      };
    } catch (error) {
      console.error('Error fetching user sessions:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  // Create multiple sessions for a class
  async createSessions(classId, sessions) {
    try {
      const response = await apiClient.post('/class-sessions', {
        class_id: classId,
        sessions: sessions
      });

      // Clear cache for this class
      this.clearCache(classId);

      return {
        success: true,
        data: response.sessions || [],
        message: response.message
      };
    } catch (error) {
      console.error('Error creating class sessions:', error);
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  // Update a single session
  async updateSession(sessionId, updates) {
    try {
      const response = await apiClient.put(`/class-sessions/${sessionId}`, updates);

      // Clear entire cache since we don't know which class this session belongs to
      this.clearCache();

      return {
        success: true,
        data: response.session,
        message: response.message
      };
    } catch (error) {
      console.error('Error updating session:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete a session
  async deleteSession(sessionId) {
    try {
      const response = await apiClient.delete(`/class-sessions/${sessionId}`);

      // Clear entire cache since we don't know which class this session belongs to
      this.clearCache();

      return {
        success: true,
        message: response.message,
        action: response.action // 'deleted' or 'cancelled'
      };
    } catch (error) {
      console.error('Error deleting session:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Check for conflicts with existing sessions
  async checkConflicts(classId, proposedSessions) {
    try {
      // Get existing sessions for comparison
      const existingSessions = await this.getUserSessions();
      
      if (!existingSessions.success) {
        return {
          success: false,
          error: 'Could not check for conflicts'
        };
      }

      const conflicts = [];
      
      proposedSessions.forEach(proposed => {
        const proposedDate = proposed.session_date || proposed.date;
        const proposedTime = proposed.start_time;
        
        existingSessions.data.forEach(existing => {
          // Skip sessions from the same class (unless we're updating)
          if (existing.class_id === classId) return;
          
          if (existing.session_date === proposedDate && 
              existing.start_time === proposedTime &&
              existing.status !== 'cancelled') {
            conflicts.push({
              date: proposedDate,
              time: proposedTime,
              conflictWith: existing.class_title
            });
          }
        });
      });

      return {
        success: true,
        conflicts: conflicts,
        hasConflicts: conflicts.length > 0
      };
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Helper method to format sessions for the DatePicker component
  formatSessionsForDatePicker(sessions) {
    return sessions.map(session => {
      // Use session_date directly if it's already in YYYY-MM-DD format
      // Avoid creating Date object to prevent timezone issues
      const dateStr = session.session_date;
      
      return {
        date: dateStr,
        start_time: session.start_time,
        duration: session.duration || 60,
        id: session.id,
        status: session.status
      };
    });
  }

  // Helper method to prepare sessions for API submission
  formatDatesForAPI(selectedDates, defaultDuration = 60) {
    return selectedDates.map(dateObj => {
      if (typeof dateObj === 'string') {
        // Simple date string, use default time and duration
        return {
          session_date: dateObj,
          start_time: '10:00',
          duration: defaultDuration
        };
      } else {
        // Full date object with time and duration
        return {
          session_date: dateObj.date,
          start_time: dateObj.start_time || '10:00',
          duration: dateObj.duration || defaultDuration
        };
      }
    });
  }

  // Get sessions within a date range (for calendar views)
  async getSessionsInRange(startDate, endDate, status = null) {
    const params = {
      startDate,
      endDate
    };
    
    if (status) {
      params.status = status;
    }

    return await this.getUserSessions(params);
  }

  // Bulk update sessions (for rescheduling multiple sessions)
  async bulkUpdateSessions(updates) {
    try {
      const results = await Promise.allSettled(
        updates.map(update => this.updateSession(update.id, update.changes))
      );

      const successful = results.filter(result => result.status === 'fulfilled' && result.value.success);
      const failed = results.filter(result => result.status === 'rejected' || !result.value.success);

      return {
        success: failed.length === 0,
        successful: successful.length,
        failed: failed.length,
        errors: failed.map(f => f.reason || f.value?.error)
      };
    } catch (error) {
      console.error('Error in bulk update:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Export singleton instance
const classSessionsService = new ClassSessionsService();
export default classSessionsService;